import { useState } from "react";
import { useCancellations, type Cancellation, type CancelBy } from "../../hooks/useCancellations";
import { useCancelFilter, srcGroup } from "../../hooks/useCancelFilter";
import { DcCancelBar } from "./DcCancelBar";
import { PlayersBadge } from "../../components/shared";
import css from "./dcSections.module.css";

/**
 * DC «Отмены» (screen-05). Отменённые брони из /api/bookings/cancelled за период
 * календаря шапки; «Вернуть» возвращает бронь в исходный слот (PUT /restore).
 *
 * 11.09 (Даша через владельца): над списком — третья строка с фильтрами, поиском
 * и сортировкой (DcCancelBar). Фильтрует уже загруженное: сервер по имени искать
 * не может, имя и телефон приходят расшифрованными только сюда.
 */
export function DcCancellationsSection(
  { onToast, from, to }: { onToast: (m: string) => void; from?: string | null; to?: string | null },
) {
  /* 25.09 (владелец: «календарь выбирает период чего?»). Раньше — только период
     ИГРЫ: вкладка отвечала «каких игр мы недосчитались в эти дни». На вопрос
     «сколько нам наотменяли за неделю» она не отвечала — игру могли отменить
     сегодня, а стояла она на октябрь. Умолчание прежнее, чтобы у Даши ничего не
     переехало само. */
  const [by, setBy] = useState<CancelBy>("game");
  const { items, loading, error, restore } = useCancellations(from, to, by);
  const [busy, setBusy] = useState<string | null>(null);
  const [dockH, setDockH] = useState(0);
  const f = useCancelFilter(items);

  const onRestore = async (c: Cancellation) => {
    setBusy(String(c.id));
    const res = await restore(c);
    setBusy(null);
    onToast(res.ok ? "Бронь возвращена" : res.error ?? "Не удалось вернуть");
  };

  const shown = f.shown;
  return (
    <div className={css.main}>
      <DcCancelBar
        query={f.query} onQuery={f.setQuery}
        typeOpts={f.typeOpts} types={f.types} onTypes={f.setTypes}
        sourceOpts={f.sourceOpts} sources={f.sources} onSources={f.setSources}
        branchOpts={f.branchOpts} branches={f.branches} onBranches={f.setBranches}
        sort={f.sort} dir={f.dir} onSort={f.pickSort}
        activeCount={f.activeCount} onReset={f.reset}
        onDockHeight={setDockH}
      />
      <div className={css.byRow} role="group" aria-label="Период считать по">
        <span className={css.byLbl}>Период по</span>
        <button type="button" className={by === "game" ? css.byOn : css.byOff}
          aria-pressed={by === "game"} onClick={() => setBy("game")}>дате игры</button>
        <button type="button" className={by === "cancelled" ? css.byOn : css.byOff}
          aria-pressed={by === "cancelled"} onClick={() => setBy("cancelled")}>дате отмены</button>
        <span className={css.byHint}>
          {by === "game"
            ? "какие игры не состоялись в эти дни"
            : "сколько отмен случилось за эти дни"}
        </span>
      </div>
      {error ? (
        <div className={css.err}>Ошибка загрузки отмен: {error}</div>
      ) : loading ? (
        <div className={css.loading}>Загрузка…</div>
      ) : items.length === 0 ? (
        <div className={css.empty}>
          За выбранный период отмен нет — раздвиньте даты в календаре сверху.
        </div>
      ) : (
        <>
          <div className={css.counter}>
            Найдено: <b>{shown.length}</b> {plural(shown.length)}
            {shown.length !== items.length ? <span> из {items.length}</span> : null}
            {from && to ? <span> · {fmtDM(from)}–{fmtDM(to)}</span> : null}
          </div>
          {shown.length === 0 ? (
            <div className={css.empty}>
              Ничего не найдено. Ищем только внутри периода сверху — раздвиньте даты или{" "}
              <button type="button" className={css.emptyLink} onClick={f.reset}>снимите фильтры</button>.
            </div>
          ) : null}
          {shown.map((c) => (
            <CancelCard key={String(c.id)} c={c} busy={busy === String(c.id)} onRestore={() => onRestore(c)} />
          ))}
        </>
      )}
      {/* Клавиатура накрывает низ списка — спейсер держит последние карточки доступными. */}
      <div style={{ height: dockH }} aria-hidden />
    </div>
  );
}

function CancelCard({ c, busy, onRestore }: { c: Cancellation; busy: boolean; onRestore: () => void }) {
  const time = (c.custom_time || c.time || "").slice(0, 5);
  const qname = c.special_version_title || c.short_name || c.quest_name || "—";
  const client = (c.client_name ?? "").trim();
  const reason = (c.cancel_reason ?? "").trim();
  return (
    <div className={css.cxCard}>
      <div className={css.cxRow1}>
        <span className={css.cxQname}>{qname}</span>
        <span className={css.cxTm}>{time}</span>
        <span className={css.cxDt}>{fmtDM(c.date)}</span>
        <span className={css.pillCancel}>Отмена</span>
      </div>
      <div className={css.cxRow2}>
        {client ? <span className={css.cxClient}>{client}</span> : null}
        {/* 05.09 (владелец): стандартный бейдж игроков; у ДК есть доступ — клик открывает карточку клиента брони */}
        {c.players ? <PlayersBadge count={c.players} bookingId={Number(c.id)} fallbackName={client} size="sm" /> : null}
        {c.branch_name ? <span className={css.cxBranch}>{c.branch_name}</span> : null}
        {c.source ? <span className={css.cxSrc} title={c.source}>{srcGroup(c.source)}</span> : null}
        {c.phone ? <span className={css.cxPhone}>{c.phone}</span> : null}
      </div>
      <div className={`${css.cxReason} ${reason ? "" : css.cxReasonEmpty}`}>
        <b>Причина:</b> {reason || "не указана"}
        {c.type === "block" && c.block_reason ? <> · <b>блок:</b> {c.block_reason}</> : null}
      </div>
      <div className={css.cxRow4}>
        <span className={css.cxBy}>
          {c.cancelled_by_name ? <>Отменил: <b>{c.cancelled_by_name}</b></> : null}
          {c.cancelled_at ? <span className={css.when}> · {fmtWhen(c.cancelled_at)}</span> : null}
        </span>
        <button type="button" className={css.btnRestore} onClick={onRestore} disabled={busy}>
          <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="square" aria-hidden="true"><path d="M2 6a4 4 0 1 0 1.5-3.1" /><path d="M2 2v3h3" /></svg>
          {busy ? "…" : "Вернуть"}
        </button>
      </div>
    </div>
  );
}

function plural(n: number): string {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return "отмена";
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return "отмены";
  return "отмен";
}
function fmtDM(date?: string | null): string {
  if (!date) return "";
  const [, m, d] = date.split("-");
  return m && d ? `${d}.${m}` : date;
}
function fmtWhen(iso: string): string {
  const date = iso.slice(0, 10);
  const time = iso.slice(11, 16);
  return `${fmtDM(date)}${time ? ` ${time}` : ""}`;
}
